import { getGuestSpatiusAvatarExposureProperties } from "@/lib/experiments/guestSpatiusAvatar";
import {
  type FreemiumUsageState,
  getFreemiumDiscountCoupon,
  isFreemiumDiscountCohort,
} from "@/lib/freemium/usage-state";
import {
  GUEST_USAGE_TODAY_KEY,
  MAX_REGISTERED_SECONDS_PER_DAY,
  buildDebugFreemiumUsageState,
  getDateScopedUsageSeconds,
  getFreemiumDebugMode,
  getGuestTotalUsageSeconds,
  getRegisteredUsageSeconds,
  wasPreSessionOfferDismissedToday,
  wasUserCreatedToday,
} from "@/lib/livekit-agent-usage-storage";
import { applyGuestUsageToFreemiumDayIfNeeded } from "@/lib/livekit-freemium-carryover";
import type { SessionMode } from "@/lib/sessionRouting";
import { supabase } from "@/lib/supabaseClient";
import { useCallback, useEffect, useRef, useState } from "react";
import type { TimeLimitPopupContext } from "./useLiveKitAgent";

type MutableRef<T> = {
  current: T;
};

type FreemiumGateLogger = {
  info: (message: string, data?: unknown) => void;
  warn: (message: string, data?: unknown) => void;
  error: (message: string, data?: unknown) => void;
};

type PostHogLike = {
  capture: (eventName: string, properties?: Record<string, unknown>) => void;
};

type FreemiumGateUser = {
  id?: string | null;
  email?: string | null;
  created_at?: string | null;
};

type UseLiveKitFreemiumGateInput = {
  user?: FreemiumGateUser | null;
  subscribed?: boolean | null;
  maxGuestSeconds: number;
  analyticsSurface: string;
  posthogClient?: PostHogLike | null;
  authTokenCacheIdentityRef: MutableRef<string | null>;
  tokenCacheRef: MutableRef<unknown>;
  logger: FreemiumGateLogger;
};

export function useLiveKitFreemiumGate({
  user,
  subscribed,
  maxGuestSeconds,
  analyticsSurface,
  posthogClient,
  authTokenCacheIdentityRef,
  tokenCacheRef,
  logger,
}: UseLiveKitFreemiumGateInput) {
  const [showTimeLimitPopup, setShowTimeLimitPopup] = useState<boolean>(false);
  const [timeLimitPopupContext, setTimeLimitPopupContext] = useState<TimeLimitPopupContext>({
    limitType: "guest",
  });
  const [freemiumUsageState, setFreemiumUsageState] = useState<FreemiumUsageState | null>(null);
  const [showFreemiumPreSessionOffer, setShowFreemiumPreSessionOffer] = useState<boolean>(false);
  const [hasExceededGuestLimit, setHasExceededGuestLimit] = useState<boolean>(false);
  const [hasExceededRegisteredLimit, setHasExceededRegisteredLimit] = useState<boolean>(false);
  const pendingPreSessionOfferModeRef = useRef<SessionMode | null>(null);
  const hasTrackedGuestAuthenticatedRef = useRef<boolean>(false);

  const showFreemiumLimitPopup = useCallback(
    (limitType: TimeLimitPopupContext["limitType"], state?: FreemiumUsageState | null) => {
      setTimeLimitPopupContext({
        limitType,
        freemiumCohort: state?.cohort,
        qualifiedConversationCount: state?.qualifiedConversationCount,
        qualifiedConversationsThisWeek: state?.qualifiedConversationsThisWeek,
      });
      setShowTimeLimitPopup(true);
    },
    []
  );

  const resolveFreemiumUsageState = useCallback(async (): Promise<FreemiumUsageState | null> => {
    if (!user || subscribed === true) {
      const subscribedState = buildDebugFreemiumUsageState({
        cohort: "subscribed",
        offerMode: "trial",
        shouldShowPreSessionOffer: false,
      });
      setFreemiumUsageState(subscribedState);
      return subscribedState;
    }

    // Connection issuance verifies access server-side. Until the client has a
    // definitive inactive result, do not flash free-user limits or offers.
    if (subscribed !== false) return null;

    const debugMode = getFreemiumDebugMode();
    if (debugMode) {
      const debugState = buildDebugFreemiumUsageState(
        debugMode === "medium_pre_session_offer"
          ? {
              qualifiedConversationCount: 5,
              qualifiedConversationsThisWeek: 1,
              cohort: "medium_unconverted",
              offerMode: "discount",
              shouldShowPreSessionOffer: true,
            }
          : debugMode === "heavy_pre_session_offer" || debugMode === "pre_session_offer"
            ? {
                qualifiedConversationCount: 10,
                qualifiedConversationsThisWeek: 1,
                cohort: "heavy_unconverted",
                offerMode: "discount",
                shouldShowPreSessionOffer: true,
              }
            : debugMode === "weekly_limit"
              ? {
                  qualifiedConversationsThisWeek: 3,
                  weeklyRemainingConversations: 0,
                  shouldShowPreSessionOffer: false,
                }
              : debugMode === "daily_limit"
                ? {
                    dailyRemainingMinutes: 0,
                    shouldShowPreSessionOffer: false,
                  }
                : undefined
      );
      setFreemiumUsageState(debugState);
      return debugState;
    }

    try {
      let accessToken: string | undefined;
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        accessToken = session?.access_token;
      } catch (authError) {
        logger.warn("Failed to get auth session for freemium usage state", { error: authError });
      }

      const headers: HeadersInit = {};
      if (accessToken) {
        headers["Authorization"] = `Bearer ${accessToken}`;
      }

      const response = await fetch("/api/freemium/usage-state", {
        credentials: "include",
        cache: "no-store",
        headers,
      });
      if (!response.ok) throw new Error(`freemium_usage_state_http_${response.status}`);

      const serverState = (await response.json()) as FreemiumUsageState;
      const usedSeconds = user.email ? getRegisteredUsageSeconds(user.email, logger) : 0;
      const dailyRemainingMinutes = Math.max(
        0,
        (MAX_REGISTERED_SECONDS_PER_DAY - usedSeconds) / 60
      );
      const state = {
        ...serverState,
        dailyRemainingMinutes,
        shouldShowPreSessionOffer:
          isFreemiumDiscountCohort(serverState.cohort) &&
          dailyRemainingMinutes > 0 &&
          serverState.weeklyRemainingConversations > 0,
      };
      setFreemiumUsageState(state);
      return state;
    } catch (error) {
      logger.warn("Failed to fetch freemium usage state; allowing session start", { error });
      return null;
    }
  }, [logger, subscribed, user]);

  const evaluateFreemiumStartGate = useCallback(
    async (
      requestedMode: SessionMode,
      options?: { allowPreSessionOffer?: boolean }
    ): Promise<boolean> => {
      if (!user || subscribed !== false) return true;

      const state = await resolveFreemiumUsageState();

      if (
        state?.dailyRemainingMinutes === 0 ||
        (user.email &&
          getRegisteredUsageSeconds(user.email, logger) >= MAX_REGISTERED_SECONDS_PER_DAY)
      ) {
        setHasExceededRegisteredLimit(true);
        showFreemiumLimitPopup("daily", state);
        return false;
      }

      if (state?.weeklyRemainingConversations === 0) {
        showFreemiumLimitPopup("weekly", state);
        return false;
      }

      if (
        options?.allowPreSessionOffer &&
        state?.shouldShowPreSessionOffer &&
        !wasPreSessionOfferDismissedToday()
      ) {
        pendingPreSessionOfferModeRef.current = requestedMode;
        setFreemiumUsageState(state);
        setShowFreemiumPreSessionOffer(true);
        const discountCoupon = getFreemiumDiscountCoupon(state.cohort);
        posthogClient?.capture("freemium_pre_session_offer_shown", {
          offer_surface: "pre_session_modal",
          offer_mode: "discount",
          coupon_code: discountCoupon || undefined,
          freemium_cohort: state.cohort,
          qualified_conversation_count: state.qualifiedConversationCount,
          qualified_conversations_this_week: state.qualifiedConversationsThisWeek,
        });
        return false;
      }

      return true;
    },
    [logger, posthogClient, resolveFreemiumUsageState, showFreemiumLimitPopup, subscribed, user]
  );

  useEffect(() => {
    if (!user) {
      const storedUsage = getGuestTotalUsageSeconds();
      if (storedUsage >= maxGuestSeconds) {
        setHasExceededGuestLimit(true);
        setTimeLimitPopupContext({ limitType: "guest" });
      } else {
        setHasExceededGuestLimit(false);
        setShowTimeLimitPopup(false);
      }
    } else {
      setHasExceededGuestLimit(false);
      if (
        analyticsSurface === "fullscreen" &&
        posthogClient &&
        !hasTrackedGuestAuthenticatedRef.current &&
        wasUserCreatedToday(user.created_at || undefined) &&
        getGuestTotalUsageSeconds() > 0
      ) {
        hasTrackedGuestAuthenticatedRef.current = true;
        posthogClient.capture("fullscreen_guest_authenticated", {
          user_id: user.id,
          guest_seconds_used_total: getGuestTotalUsageSeconds(),
          guest_seconds_used_today: getDateScopedUsageSeconds(GUEST_USAGE_TODAY_KEY, logger),
          ...getGuestSpatiusAvatarExposureProperties(),
        });
      }
      applyGuestUsageToFreemiumDayIfNeeded({
        user,
        maxGuestSeconds,
        maxRegisteredSecondsPerDay: MAX_REGISTERED_SECONDS_PER_DAY,
        posthogClient,
        hasPosthogKey: Boolean(process.env.NEXT_PUBLIC_POSTHOG_KEY),
        logger,
      });
      if (subscribed === false && user.email) {
        const usedSecs = getRegisteredUsageSeconds(user.email, logger);
        setHasExceededRegisteredLimit(usedSecs >= MAX_REGISTERED_SECONDS_PER_DAY);
        if (usedSecs >= MAX_REGISTERED_SECONDS_PER_DAY) {
          setTimeLimitPopupContext({ limitType: "daily" });
        }
      } else {
        setHasExceededRegisteredLimit(false);
      }
      setShowTimeLimitPopup(false);
    }

    const nextTokenCacheIdentity = user ? `user:${user.id || user.email || "unknown"}` : "guest";
    const previousTokenCacheIdentity = authTokenCacheIdentityRef.current;

    if (previousTokenCacheIdentity !== nextTokenCacheIdentity) {
      authTokenCacheIdentityRef.current = nextTokenCacheIdentity;
      tokenCacheRef.current = null;

      if (previousTokenCacheIdentity !== null) {
        logger.info("Auth identity changed, clearing LiveKit token cache", {
          previousIdentity: previousTokenCacheIdentity,
          nextIdentity: nextTokenCacheIdentity,
        });
      }
    }
  }, [
    analyticsSurface,
    authTokenCacheIdentityRef,
    logger,
    maxGuestSeconds,
    posthogClient,
    subscribed,
    tokenCacheRef,
    user,
  ]);

  return {
    showTimeLimitPopup,
    setShowTimeLimitPopup,
    timeLimitPopupContext,
    setTimeLimitPopupContext,
    freemiumUsageState,
    setFreemiumUsageState,
    showFreemiumPreSessionOffer,
    setShowFreemiumPreSessionOffer,
    hasExceededGuestLimit,
    setHasExceededGuestLimit,
    hasExceededRegisteredLimit,
    setHasExceededRegisteredLimit,
    pendingPreSessionOfferModeRef,
    showFreemiumLimitPopup,
    evaluateFreemiumStartGate,
  };
}
